import * as THREE from 'three';
import { LineSegmentsGeometry } from 'three/examples/jsm/lines/LineSegmentsGeometry.js';
import { edgeLines, markOverlay, setEdgeStyle } from './edges';
import type { EdgeLines } from './edges';
import { cssColor, edgeColor, sectionCapColor } from './palette';
import { MAX_CAST_TRIANGLES, isModelMesh, triangleCount } from './picking';

export type SectionAxis = 'x' | 'y' | 'z';

export interface SectionSettings {
  axis: SectionAxis;
  /** Where the cut sits across the model's extent on that axis, 0 to 1. */
  position: number;
  flipped: boolean;
  /** Degrees about the other two axes, in x, y, z order, the first turn applied first. */
  tilt: [number, number];
  size: number;
}

export interface SectionPose {
  center: THREE.Vector3;
  /** Points out of the cut face, into the half that is cut away. */
  normal: THREE.Vector3;
  along: THREE.Vector3;
  /** The axes `tilt[0]` and `tilt[1]` turn the cut about at its current tilt. */
  hinges: [THREE.Vector3, THREE.Vector3];
  orientation: THREE.Quaternion;
  arrowLength: number;
}

export const SECTION_TILT_LIMIT = 75;
export const SECTION_SIZE_MIN = 0.25;
export const SECTION_SIZE_MAX = 2;
export const SECTION_SIZE_STEP = 1.15;
export const DEFAULT_SECTION: SectionSettings = {
  axis: 'x',
  position: 0.5,
  flipped: false,
  tilt: [0, 0],
  size: 1,
};

const AXIS_VECTORS: Record<SectionAxis, THREE.Vector3> = {
  x: new THREE.Vector3(1, 0, 0),
  y: new THREE.Vector3(0, 1, 0),
  z: new THREE.Vector3(0, 0, 1),
};
const TILT_AXES: Record<SectionAxis, [SectionAxis, SectionAxis]> = {
  x: ['y', 'z'],
  y: ['x', 'z'],
  z: ['x', 'y'],
};
// Which way the cut face looks unflipped: towards the opening front-right-above
// view, so the section is visible the moment it is turned on.
const FACING: Record<SectionAxis, number> = { x: 1, y: -1, z: 1 };
const CAP_MARGIN = 1.15;
const HATCH_PX = 32;
const HATCHES_ACROSS_FRAME = 48;
const PLANE_Z = new THREE.Vector3(0, 0, 1);
const OUTLINE_PX = 2.5;
const FRAME_PX = 1.25;
const FRAME_OPACITY = 0.5;
// At either end of the slider the plane stands clear of the part by this share
// of its size, so nothing is cut and no face lies in the plane.
const END_CLEARANCE = 0.01;
const ARROW_SHARE = 0.2;

const frameColor = (isDark: boolean): number => (isDark ? 0xd4b47a : 0xb88a3e);
const hatchColor = (isDark: boolean): string => `rgba(70, 48, 18, ${isDark ? 0.5 : 0.42})`;

const clampSize = (size: number): number =>
  THREE.MathUtils.clamp(size, SECTION_SIZE_MIN, SECTION_SIZE_MAX);

const largestSide = (box: THREE.Box3): number => {
  const size = box.getSize(new THREE.Vector3());
  return Math.max(size.x, size.y, size.z);
};

export const tiltAxes = (axis: SectionAxis): [SectionAxis, SectionAxis] => TILT_AXES[axis];

export function clampTilt(degrees: number): number {
  return THREE.MathUtils.clamp(degrees, -SECTION_TILT_LIMIT, SECTION_TILT_LIMIT);
}

export function resizeSection(settings: SectionSettings, grow: boolean): SectionSettings {
  const size = grow ? settings.size * SECTION_SIZE_STEP : settings.size / SECTION_SIZE_STEP;
  return { ...settings, size: clampSize(size) };
}

export function sectionOffset(settings: SectionSettings, box: THREE.Box3): number {
  const along = AXIS_VECTORS[settings.axis];
  const min = box.min.dot(along);
  const max = box.max.dot(along);
  if (settings.position <= 0) return min - largestSide(box) * END_CLEARANCE;
  if (settings.position >= 1) return max + largestSide(box) * END_CLEARANCE;
  return THREE.MathUtils.lerp(min, max, settings.position);
}

export function positionForOffset(axis: SectionAxis, offset: number, box: THREE.Box3): number {
  const along = AXIS_VECTORS[axis];
  const min = box.min.dot(along);
  const extent = box.max.dot(along) - min;
  return extent > 0 ? THREE.MathUtils.clamp((offset - min) / extent, 0, 1) : 0.5;
}

export function sectionPose(settings: SectionSettings, box: THREE.Box3): SectionPose {
  const along = AXIS_VECTORS[settings.axis].clone();
  const [first, second] = TILT_AXES[settings.axis];
  const [inner, outer] = settings.tilt.map((degrees) => THREE.MathUtils.degToRad(clampTilt(degrees)));
  const outerTurn = new THREE.Quaternion().setFromAxisAngle(AXIS_VECTORS[second], outer);
  const tilt = new THREE.Quaternion()
    .setFromAxisAngle(AXIS_VECTORS[first], inner)
    .premultiply(outerTurn);
  const facing = FACING[settings.axis] * (settings.flipped ? -1 : 1);
  const center = box.getCenter(new THREE.Vector3());
  center.addScaledVector(along, sectionOffset(settings, box) - center.dot(along));
  return {
    center,
    normal: along.clone().applyQuaternion(tilt).multiplyScalar(facing),
    along,
    hinges: [AXIS_VECTORS[first].clone().applyQuaternion(outerTurn), AXIS_VECTORS[second].clone()],
    orientation: new THREE.Quaternion().setFromUnitVectors(PLANE_Z, along).premultiply(tilt),
    arrowLength: largestSide(box) * ARROW_SHARE * clampSize(settings.size),
  };
}

function hatchTexture(isDark: boolean): THREE.Texture | null {
  const canvas = document.createElement('canvas');
  canvas.width = HATCH_PX;
  canvas.height = HATCH_PX;
  const context = canvas.getContext('2d');
  if (!context) return null;
  context.fillStyle = cssColor(sectionCapColor(isDark));
  context.fillRect(0, 0, HATCH_PX, HATCH_PX);
  context.strokeStyle = hatchColor(isDark);
  context.lineWidth = 3;
  for (const offset of [-HATCH_PX, 0, HATCH_PX]) {
    context.beginPath();
    context.moveTo(offset, HATCH_PX);
    context.lineTo(offset + HATCH_PX, 0);
    context.stroke();
  }
  const texture = new THREE.CanvasTexture(canvas);
  texture.wrapS = THREE.RepeatWrapping;
  texture.wrapT = THREE.RepeatWrapping;
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.anisotropy = 4;
  return texture;
}

function stencilMaterial(side: THREE.Side, op: THREE.StencilOp, plane: THREE.Plane) {
  return new THREE.MeshBasicMaterial({
    side,
    colorWrite: false,
    depthWrite: false,
    depthTest: false,
    stencilWrite: true,
    stencilFunc: THREE.AlwaysStencilFunc,
    stencilFail: op,
    stencilZFail: op,
    stencilZPass: op,
    clippingPlanes: [plane],
  });
}

const localPlane = new THREE.Plane();
const corners = [new THREE.Vector3(), new THREE.Vector3(), new THREE.Vector3()];
const sides = [0, 0, 0];
const crossing = new THREE.Vector3();

// A vertex on the plane counts as kept, so every cut triangle crosses it on
// exactly two of its edges and yields one segment.
function appendCut(mesh: THREE.Mesh, toLocal: THREE.Matrix4, plane: THREE.Plane, out: number[]): void {
  const position = mesh.geometry.getAttribute('position');
  if (!position) return;
  const index = mesh.geometry.index;
  const count = index ? index.count : position.count;
  localPlane.copy(plane).applyMatrix4(toLocal);
  for (let i = 0; i + 2 < count; i += 3) {
    for (let k = 0; k < 3; k++) {
      corners[k].fromBufferAttribute(position, index ? index.getX(i + k) : i + k);
      sides[k] = localPlane.distanceToPoint(corners[k]);
    }
    for (let k = 0; k < 3; k++) {
      const next = (k + 1) % 3;
      if ((sides[k] >= 0) === (sides[next] >= 0)) continue;
      crossing.lerpVectors(corners[k], corners[next], sides[k] / (sides[k] - sides[next]));
      crossing.applyMatrix4(mesh.matrixWorld);
      out.push(crossing.x, crossing.y, crossing.z);
    }
  }
}

function frameSegments(): THREE.BufferGeometry {
  const corners = [
    [-0.5, -0.5],
    [0.5, -0.5],
    [0.5, 0.5],
    [-0.5, 0.5],
  ].map(([x, y]) => new THREE.Vector3(x, y, 0));
  const points = corners.flatMap((corner, i) => [corner, corners[(i + 1) % corners.length]]);
  return new THREE.BufferGeometry().setFromPoints(points);
}

// Caps come from the stencil buffer: along any ray through the cut, back faces
// outnumber front faces exactly where the ray is inside the solid.
export class SectionView {
  readonly plane = new THREE.Plane();
  /** The profile of the cut, drawn over the rim of the cap. */
  readonly outline: EdgeLines;
  private readonly root = new THREE.Group();
  private readonly group = new THREE.Group();
  private readonly cap: THREE.Mesh<THREE.PlaneGeometry, THREE.MeshStandardMaterial>;
  private readonly frame: EdgeLines;
  private readonly backFaces: THREE.MeshBasicMaterial;
  private readonly frontFaces: THREE.MeshBasicMaterial;
  private readonly stencils: THREE.Mesh[] = [];
  private readonly faces: { mesh: THREE.Mesh; toLocal: THREE.Matrix4 }[] = [];
  private readonly box = new THREE.Box3();
  private readonly capCenter = new THREE.Vector3();
  private readonly arrowTip = new THREE.Vector3();
  private readonly outlinedPlane = new THREE.Plane(new THREE.Vector3(), Number.NaN);
  private outlinedCapped = false;
  private settings: SectionSettings | null = null;
  private capped = true;

  constructor(isDark: boolean) {
    this.backFaces = stencilMaterial(THREE.BackSide, THREE.IncrementWrapStencilOp, this.plane);
    this.frontFaces = stencilMaterial(THREE.FrontSide, THREE.DecrementWrapStencilOp, this.plane);
    const hatch = hatchTexture(isDark);
    this.cap = new THREE.Mesh(
      new THREE.PlaneGeometry(1, 1),
      new THREE.MeshStandardMaterial({
        // The hatch is drawn on the cap colour already.
        color: hatch ? 0xffffff : sectionCapColor(isDark),
        map: hatch,
        roughness: 0.7,
        metalness: 0,
        side: THREE.DoubleSide,
        stencilWrite: true,
        stencilRef: 0,
        stencilFunc: THREE.NotEqualStencilFunc,
        stencilFail: THREE.ReplaceStencilOp,
        stencilZFail: THREE.ReplaceStencilOp,
        stencilZPass: THREE.ReplaceStencilOp,
      }),
    );
    this.cap.renderOrder = 2;
    this.cap.onAfterRender = (renderer) => renderer.clearStencil();
    this.frame = edgeLines(frameSegments(), FRAME_PX);
    setEdgeStyle(this.frame, frameColor(isDark), FRAME_OPACITY);
    this.group.add(markOverlay(this.cap), this.frame);
    this.outline = edgeLines(
      new THREE.BufferGeometry().setAttribute('position', new THREE.Float32BufferAttribute([], 3)),
      OUTLINE_PX,
    );
    setEdgeStyle(this.outline, edgeColor(isDark), 1);
    // Lies in the cap, so it is pulled forward to win the depth test there.
    this.outline.material.polygonOffset = true;
    this.outline.material.polygonOffsetFactor = -1;
    this.outline.material.polygonOffsetUnits = -4;
    this.outline.renderOrder = 3;
    this.outline.visible = false;
    this.root.add(this.group, this.outline);
    this.root.visible = false;
  }

  get object(): THREE.Object3D {
    return this.root;
  }

  get active(): boolean {
    return this.settings !== null;
  }

  attach(model: THREE.Object3D, box: THREE.Box3): void {
    this.detach();
    this.box.copy(box);
    model.updateWorldMatrix(true, true);
    const meshes: THREE.Mesh[] = [];
    model.traverse((child) => {
      if (isModelMesh(child)) meshes.push(child);
    });
    // Every slider tick re-cuts every triangle; past this a drag would stutter.
    const outlined = triangleCount(model) <= MAX_CAST_TRIANGLES;
    for (const mesh of meshes) {
      if (outlined) this.faces.push({ mesh, toLocal: mesh.matrixWorld.clone().invert() });
      for (const material of [this.backFaces, this.frontFaces]) {
        const stencil = markOverlay(new THREE.Mesh(mesh.geometry, material));
        stencil.renderOrder = 1;
        stencil.visible = false;
        mesh.add(stencil);
        this.stencils.push(stencil);
      }
    }
    this.place();
  }

  set(settings: SectionSettings | null, capped: boolean): void {
    this.settings = settings;
    this.capped = capped;
    this.root.visible = settings !== null;
    this.cap.visible = capped;
    for (const stencil of this.stencils) stencil.visible = settings !== null && capped;
    this.place();
  }

  center(target: THREE.Vector3): THREE.Vector3 {
    return target.copy(this.capCenter);
  }

  /** Where the gizmo's arrow ends. */
  anchor(target: THREE.Vector3): THREE.Vector3 {
    return target.copy(this.arrowTip);
  }

  /** Clips every material under `model`; rerun whenever those materials change. */
  applyTo(model: THREE.Object3D): void {
    const planes = this.settings ? [this.plane] : null;
    model.traverse((child) => {
      const drawable = child as THREE.Mesh;
      if (!drawable.material || drawable.material === this.backFaces) return;
      if (drawable.material === this.frontFaces) return;
      const materials = Array.isArray(drawable.material) ? drawable.material : [drawable.material];
      for (const material of materials) material.clippingPlanes = planes;
    });
  }

  dispose(): void {
    this.detach();
    this.backFaces.dispose();
    this.frontFaces.dispose();
    this.cap.geometry.dispose();
    this.cap.material.map?.dispose();
    this.cap.material.dispose();
    this.frame.geometry.dispose();
    this.frame.material.dispose();
    this.outline.geometry.dispose();
    this.outline.material.dispose();
  }

  private detach(): void {
    for (const stencil of this.stencils) stencil.removeFromParent();
    this.stencils.length = 0;
    this.faces.length = 0;
    this.outlinedPlane.constant = Number.NaN;
  }

  private drawOutline(): void {
    const capped = this.settings !== null && this.capped;
    if (capped === this.outlinedCapped && this.plane.equals(this.outlinedPlane)) return;
    this.outlinedPlane.copy(this.plane);
    this.outlinedCapped = capped;
    const positions: number[] = [];
    if (capped) {
      for (const { mesh, toLocal } of this.faces) appendCut(mesh, toLocal, this.plane, positions);
    }
    this.outline.visible = positions.length > 0;
    if (!this.outline.visible) return;
    this.outline.geometry.dispose();
    this.outline.geometry = new LineSegmentsGeometry().setPositions(positions);
  }

  private place(): void {
    if (!this.settings || this.box.isEmpty()) return;
    const pose = sectionPose(this.settings, this.box);
    // A plane keeps normal·p + constant >= 0: the half behind the cut face.
    this.plane.setFromNormalAndCoplanarPoint(pose.normal.clone().negate(), pose.center);
    this.capCenter.copy(pose.center);
    this.arrowTip.copy(pose.center).addScaledVector(pose.normal, pose.arrowLength);

    const largest = largestSide(this.box);
    const frameSpan = largest * CAP_MARGIN * clampSize(this.settings.size);
    // Wide enough for any tilt and position, and fixed per model so the hatch
    // does not swim while the cut is dragged; the stencil trims it to the part.
    const capSpan = 2 * (this.box.min.distanceTo(this.box.max) + largest * END_CLEARANCE);
    this.group.position.copy(pose.center);
    this.group.quaternion.copy(pose.orientation);
    this.cap.scale.set(capSpan, capSpan, 1);
    this.frame.scale.set(frameSpan, frameSpan, 1);
    const hatches = (HATCHES_ACROSS_FRAME * capSpan) / Math.max(largest * CAP_MARGIN, Number.EPSILON);
    this.cap.material.map?.repeat.set(hatches, hatches);
    this.drawOutline();
  }
}
